import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import * as sdk from 'pkg/core/sdk';
import * as ls from 'pkg/local_storage';

import { Event, trackEvent } from 'components/analytics';

export interface InviteEmailParams {
	emailSubject?: string;
	emailContent?: string;
}

export enum RefusalCode {
	InviteRequired = 'waitlist_invite_required',
	InvitedFull = 'waitlist_invited_full',
	ResendWaitlisted = 'waitlist_resend_waitlisted',
}

export interface WaitlistTransition {
	eventGroupIds: number[];
	userIds: number[];
}

export function trackTransition(
	event: Event,
	organizationId: number,
	formId: number,
	{ eventGroupIds, userIds }: WaitlistTransition,
	props: { [key: string]: unknown } = {}
): void {
	// Ids as strings, like every other event's organization_id and form_id, so
	// Mixpanel sees one type per property. form_id here is the form's numeric
	// id, which REG-1286's reports pair invites and bookings by.
	trackEvent(event, {
		organization_id: String(organizationId),
		form_id: String(formId),
		event_group_ids: eventGroupIds,
		user_ids: userIds,
		child_count: userIds.length,
		...props,
	});
}

export function withoutInvalidInputPrefix(error?: string): string {
	const message = (error ?? '').replace(/^invalid input: /, '');

	return message.charAt(0).toUpperCase() + message.slice(1);
}

export async function errorMessage(request: Response): Promise<string> {
	try {
		const body = await request.json();

		return withoutInvalidInputPrefix(body?.error);
	} catch {
		return '';
	}
}

export async function errorCode(request: Response): Promise<string> {
	try {
		const body = await request.json();

		return body?.code ?? '';
	} catch {
		return '';
	}
}

async function send(
	request: () => Promise<Response>,
	failureTitle: string
): Promise<boolean> {
	let response: Response;

	try {
		response = await request();
	} catch {
		flashActions.show({
			title: failureTitle,
			message: t('Please try again.'),
		});

		return false;
	}

	if (!response.ok) {
		flashActions.show({
			title: failureTitle,
			message: await errorMessage(response),
		});
	}

	return response.ok;
}

export async function invite(
	organizationId: number,
	formId: number,
	entryIds: number[],
	transition: WaitlistTransition,
	emailParams: InviteEmailParams = {}
): Promise<boolean> {
	const ok = await send(
		() =>
			sdk.post(
				endpoints.FormEventGroups.InviteFromWaitlist(organizationId, formId),
				{},
				{ entryIds, ...emailParams }
			),
		t("Couldn't send the invites")
	);

	if (ok) {
		trackTransition(Event.WaitlistInvited, organizationId, formId, transition);
	}

	return ok;
}

export async function revokeInvites(
	organizationId: number,
	formId: number,
	entryIds: number[],
	transition: WaitlistTransition
): Promise<boolean> {
	const ok = await send(
		() =>
			sdk.post(
				endpoints.FormEventGroups.RevokeWaitlistInvites(organizationId, formId),
				{},
				{ entryIds }
			),
		t("Couldn't revoke the invite")
	);

	if (ok) {
		trackTransition(
			Event.WaitlistInviteRevoked,
			organizationId,
			formId,
			transition
		);
	}

	return ok;
}

export async function declineEntries(
	organizationId: number,
	formId: number,
	entryIds: number[],
	transition: WaitlistTransition
): Promise<boolean> {
	const ok = await send(
		() =>
			sdk.post(
				endpoints.FormEventGroups.DeclineWaitlistEntries(
					organizationId,
					formId
				),
				{},
				{ entryIds }
			),
		t("Couldn't set as declined")
	);

	if (ok) {
		trackTransition(
			Event.WaitlistSetAsDeclined,
			organizationId,
			formId,
			transition
		);
	}

	return ok;
}

export async function removeEntries(
	organizationId: number,
	formId: number,
	entryIds: number[],
	transition: WaitlistTransition
): Promise<boolean> {
	const ok = await send(
		() =>
			sdk.post(
				endpoints.FormEventGroups.RemoveWaitlistEntries(organizationId, formId),
				{},
				{ entryIds }
			),
		t("Couldn't remove them from the waitlist")
	);

	if (ok) {
		trackTransition(Event.WaitlistRemoved, organizationId, formId, transition);
	}

	return ok;
}

export function trackInviteDeclined(
	organizationId: number,
	formId: number,
	invitation: models.formSubmission.FormSubmission,
	invitedEventGroupIds: number[] = []
): void {
	if (invitedEventGroupIds.length === 0) {
		return;
	}

	trackTransition(Event.WaitlistInviteDeclined, organizationId, formId, {
		eventGroupIds: invitedEventGroupIds,
		userIds: [invitation.submittedForUserId],
	});
}

type InviteBookings = { [submissionId: number]: number[] };

function readInviteBookings(): InviteBookings {
	try {
		return (
			ls.getJsonItem<InviteBookings>(
				ls.LocalStorageKeys.WaitlistInviteBookings
			) ?? {}
		);
	} catch {
		return {};
	}
}

function writeInviteBookings(bookings: InviteBookings): void {
	try {
		if (Object.keys(bookings).length === 0) {
			ls.removeItem(ls.LocalStorageKeys.WaitlistInviteBookings);
		} else {
			ls.setJsonItem(ls.LocalStorageKeys.WaitlistInviteBookings, bookings);
		}
	} catch {
		return;
	}
}

export function rememberInviteBooking(
	submissionId: number,
	selectedEventGroupIds: number[],
	invitedEventGroupIds: number[] = []
): void {
	const eventGroupIds = selectedEventGroupIds.filter((id) =>
		invitedEventGroupIds.includes(id)
	);

	if (eventGroupIds.length === 0) {
		return;
	}

	writeInviteBookings({
		...readInviteBookings(),
		[submissionId]: eventGroupIds,
	});
}

export function trackInviteBooked(
	submission: models.formSubmission.FormSubmission
): void {
	const bookings = readInviteBookings();
	const eventGroupIds = bookings[submission.id];

	if (!eventGroupIds?.length) {
		return;
	}

	delete bookings[submission.id];
	writeInviteBookings(bookings);

	trackTransition(
		Event.WaitlistInviteBooked,
		submission.form?.groupId,
		submission.formId,
		{ eventGroupIds, userIds: [submission.submittedForUserId] },
		{ paid: !!submission.orderId }
	);
}
